import isEmpty from "lodash/isEmpty";
import { useFillAndBook } from "PFApp/hooks/use_fill_and_book";
import { useFillAndBookCustom } from "PFApp/hooks/use_fill_and_book_custom";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Tooltip } from "PFComponents/tooltip";
import { Activity, Id } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./fill_and_book_button.module.scss";

type FillAndBookButtonProps = {
  profileId: Id;
  activity: Activity;
  shortlistId?: Id;
  isFillButtonDisabled: boolean;
  disabled?: boolean;
  small?: boolean;
  named?: boolean;
};

export const FillAndBookButton = ({
  profileId,
  shortlistId,
  activity,
  isFillButtonDisabled,
  disabled,
  small,
  named = true
}: FillAndBookButtonProps) => {
  const { t } = useTranslation("translation");
  const { handleFillAndBook, isLoading: isFillAndBookLoading } = useFillAndBook();
  const { handleCustomFillAndBook, isLoading: isCustomFillAndBookLoading } = useFillAndBookCustom();
  const isLoading = isFillAndBookLoading || isCustomFillAndBookLoading;

  const isActivityWithoutAvailability = isEmpty(activity?.metadata.availability);
  const isCompleted = activity?.state === "complete";

  const handleDropdownOptionClick = (item) => {
    if (item === "custom") {
      handleCustomFillAndBook({ activity, profileId, named });
    }
  };

  const options: DropdownOption[] = useMemo(
    () => [
      {
        id: "custom_fill_and_book",
        displayElement: t("customFillAndBook"),
        item: "custom"
      }
    ],
    [t]
  );

  const isDisabled = isCompleted || isLoading || isFillButtonDisabled || disabled;

  const renderButtons = () =>
    isActivityWithoutAvailability ? (
      <Button
        text={t("customFillAndBook")}
        onClick={(event) => {
          event.stopPropagation();
          handleCustomFillAndBook({ activity, profileId, named });
        }}
        disabled={isDisabled}
        small={small}
        className={css.customFillAndBook}
      />
    ) : (
      <ButtonGroup compact autoWidth>
        <Button
          text={t("fillAndBook")}
          onClick={(event) => {
            event.stopPropagation();
            handleFillAndBook({ activity, profileId, shortlistId, named });
          }}
          disabled={isDisabled}
          small={small}
          className={css.fillAndBook}
        />
        <DropdownButton
          className={css.dropdownButton}
          icon="chevron-down"
          options={options}
          handleChange={handleDropdownOptionClick}
          floatingOptions={{ placement: "bottom-end" }}
          disabled={isDisabled}
          small={small}
        />
      </ButtonGroup>
    );

  return (
    <Tooltip content={t("bookingNotAvailable")} disabled={!isCompleted}>
      {renderButtons()}
    </Tooltip>
  );
};
